import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { User, Wallet, Transaction, ProfitRecord, OKXTicker, PriceAlert } from '../types';
import { marketService } from '../services/marketService';
import { accountService } from '../services/accountService';
import { userProfitService } from '../services/userProfitService';
import { priceAlertService } from '../services/priceAlertService';
import { authService } from '../services/authService';
import { walletService } from '../services/walletService';
import { getTelegramUser } from '../telegram';
import { AppPage, ProfitTimeRange, BarInterval } from '@/services/enums';

interface AppState {
  // 用户相关状态
  user: User | null;
  wallet: Wallet | null;
  wallets: Wallet[];

  // 认证相关状态
  isAuthenticated: boolean;
  authToken: string | null;
  authUser: any | null;

  // 交易相关状态
  transactions: Transaction[];
  profitRecords: ProfitRecord[];

  // 市场数据状态
  marketData: OKXTicker[];

  // 价格警报状态
  recentPriceAlerts: PriceAlert[];

  // UI 状态
  loading: boolean;
  error: string | null;
  currentPage: AppPage;

  // 防重复调用状态
  isFetchingMarketData: boolean;
  isAppInitialized: boolean;
  isMarketDataInitialized: boolean;
  isFetchingAvailableCoins: boolean;

  // 账户数据状态
  accountPositionsHistory: any | null;
  positionStatistics: {
    todayPnL: number;
    totalPnL: number;
    totalTrades: number;
    winRate: number;
  } | null;
  isFetchingAccountData: boolean;

  // 每日收益数据状态
  dailyProfits: any[] | null;
  isFetchingDailyProfits: boolean;

  // 用户收益数据状态
  userProfitStats: {
    totalAssets: number;
    totalProfit: number;
    todayProfit: number;
    profitRate: number;
  } | null;
  userProfitHistory: any[] | null;
  fundPoolStats: any | null;
  isFetchingUserProfit: boolean;
  isFetchingUserProfitHistory: boolean;
  isFetchingFundPool: boolean;
  isFetchingWallet: boolean;
  isFetchingTransactions: boolean;

  // K线回放状态
  playbackSymbol: string;
  playbackRange: { from: number; to: number; bar: BarInterval } | null;

  // 认证状态
  isAuthenticating: boolean;

  // Actions - 用户相关
  setUser: (user: User | null) => void;
  setWallet: (wallet: Wallet | null) => void;

  // Actions - 交易相关
  setTransactions: (transactions: Transaction[]) => void;
  addTransaction: (transaction: Transaction) => void;
  setProfitRecords: (profitRecords: ProfitRecord[]) => void;
  addProfitRecord: (profitRecord: ProfitRecord) => void;

  // Actions - 市场数据
  setMarketData: (marketData: OKXTicker[]) => void;
  fetchMarketData: () => Promise<void>;

  // Actions - 价格警报
  setRecentPriceAlerts: (alerts: PriceAlert[]) => void;
  fetchRecentPriceAlerts: (hours?: number) => Promise<void>;

  // Actions - 账户数据
  setAccountPositionsHistory: (positionsHistory: any) => void;
  setPositionStatistics: (statistics: {
    todayPnL: number;
    totalPnL: number;
    totalTrades: number;
    winRate: number;
  }) => void;
  fetchAccountData: (timeRange?: ProfitTimeRange) => Promise<void>;

  // Actions - 每日收益数据
  setDailyProfits: (dailyProfits: any[]) => void;
  fetchDailyProfits: (startDate?: string, endDate?: string) => Promise<void>;

  // Actions - 用户收益数据
  setUserProfitStats: (
    stats: {
      totalAssets: number;
      totalProfit: number;
      todayProfit: number;
      profitRate: number;
    } | null,
  ) => void;
  setUserProfitHistory: (history: any[] | null) => void;
  setFundPoolStats: (stats: any | null) => void;
  fetchUserProfitStats: () => Promise<void>;
  fetchUserProfitHistory: (days?: number) => Promise<void>;
  fetchFundPoolStats: () => Promise<void>;

  // Actions - 钱包相关
  setWallets: (wallets: Wallet[]) => void;
  fetchWallet: (currency?: string, chain?: string, sync?: boolean) => Promise<void>;
  fetchWallets: () => Promise<void>;
  createWallet: (currency?: string, chain?: string) => Promise<any>;
  deleteWallet: (id: number) => Promise<void>;
  fetchTransactions: (params?: any) => Promise<void>;

  // Actions - 认证相关
  setAuthToken: (token: string | null) => void;
  setAuthUser: (user: any | null) => void;
  setAuthenticated: (isAuthenticated: boolean) => void;
  telegramLogin: (initData: string) => Promise<boolean>;
  logout: () => void;
  checkAuthStatus: () => Promise<boolean>;

  // Actions - UI 状态
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setCurrentPage: (page: AppPage) => void;
  setPlaybackSymbol: (symbol: string) => void;
  setPlaybackRange: (range: { from: number; to: number; bar: BarInterval } | null) => void;

  // Actions - 初始化
  initializeApp: () => Promise<void>;

  // Actions - 清空状态
  clearAllState: () => void;
}

// 数据获取已移至 dataService，这里保留基本的防重复机制

export const useAppStore = create<AppState>()(
  devtools(
    (set, get) => ({
      // 初始状态
      user: null,
      wallet: null,
      wallets: [],
      transactions: [],
      profitRecords: [],
      marketData: [],
      recentPriceAlerts: [],
      loading: true,
      error: null,
      currentPage: 'dashboard',
      isFetchingMarketData: false,
      isAppInitialized: false,
      isMarketDataInitialized: false,
      isFetchingAvailableCoins: false,

      // 认证相关状态
      isAuthenticated: false,
      authToken: localStorage.getItem('authToken'),
      authUser: null,
      isAuthenticating: false,

      // 账户数据状态
      accountPositionsHistory: null,
      positionStatistics: null,
      isFetchingAccountData: false,

      // 每日收益数据状态
      dailyProfits: null,
      isFetchingDailyProfits: false,

      // 用户收益数据状态
      userProfitStats: null,
      userProfitHistory: null,
      fundPoolStats: null,
      isFetchingUserProfit: false,
      isFetchingUserProfitHistory: false,
      isFetchingFundPool: false,
      isFetchingWallet: false,
      isFetchingTransactions: false,

      // K线回放状态
      playbackSymbol: '',
      playbackRange: null,

      // 用户相关 actions
      setUser: (user) => set({ user }, false, 'setUser'),
      setWallet: (wallet) => set({ wallet }, false, 'setWallet'),
      setWallets: (wallets) => set({ wallets }, false, 'setWallets'),

      // 交易相关 actions
      setTransactions: (transactions) => set({ transactions }, false, 'setTransactions'),
      addTransaction: (transaction) =>
        set(
          (state: AppState) => ({
            transactions: [transaction, ...state.transactions],
          }),
          false,
          'addTransaction',
        ),
      setProfitRecords: (profitRecords) => set({ profitRecords }, false, 'setProfitRecords'),
      addProfitRecord: (profitRecord) =>
        set(
          (state: AppState) => ({
            profitRecords: [...state.profitRecords, profitRecord],
          }),
          false,
          'addProfitRecord',
        ),

      // 市场数据 actions
      setMarketData: (marketData) => set({ marketData }, false, 'setMarketData'),

      // 账户数据 actions
      setAccountPositionsHistory: (positionsHistory) =>
        set({ accountPositionsHistory: positionsHistory }, false, 'setAccountPositionsHistory'),
      fetchMarketData: async () => {
        const { setLoading, setError, setMarketData, isFetchingMarketData, isMarketDataInitialized } = get();

        // 防止重复调用
        if (isFetchingMarketData) {
          console.log('⏳ 市场数据正在获取中，跳过重复调用');
          return;
        }

        // 防止重复初始化
        if (isMarketDataInitialized) {
          console.log('⏳ 市场数据已经初始化，跳过重复初始化');
          return;
        }

        try {
          set({ isFetchingMarketData: true, isMarketDataInitialized: true });
          setLoading(true);
          setError(null);

          console.log('🔍 开始健康检查...');
          await marketService.healthCheck();
          console.log('✅ 健康检查通过，开始加载市场数据...');

          const response = await marketService.getMarketData();
          console.log('📊 市场数据响应:', response);

          // response 是直接的数据数组
          setMarketData(response || []);
          console.log('✅ 市场数据加载成功，数据条数:', (response || []).length);
        } catch (error) {
          console.error('❌ 获取市场数据失败:', error);
          setError('获取市场数据失败');
          // 重置初始化标志，允许重试
          set({ isMarketDataInitialized: false });
        } finally {
          setLoading(false);
          set({ isFetchingMarketData: false });
        }
      },

      // 价格警报 actions
      setRecentPriceAlerts: (recentPriceAlerts) => set({ recentPriceAlerts }, false, 'setRecentPriceAlerts'),

      fetchRecentPriceAlerts: async (hours = 24) => {
        const { setLoading, setError, setRecentPriceAlerts } = get();

        try {
          setLoading(true);
          setError(null);

          console.log(`🔍 获取最近${hours}小时的价格警报...`);
          const response = await priceAlertService.getRecentPriceAlerts({ hours });

          if (response && response.alerts) {
            setRecentPriceAlerts(response.alerts);
            console.log(`✅ 获取到${response.alerts.length}条价格警报`);
          } else {
            console.log('⚠️ 没有获取到价格警报数据');
            setRecentPriceAlerts([]);
          }
        } catch (error: any) {
          console.error('❌ 获取价格警报失败:', error.message);
          setError(`获取价格警报失败: ${error.message}`);
          setRecentPriceAlerts([]);
        } finally {
          setLoading(false);
        }
      },

      // 设置持仓统计数据
      setPositionStatistics: (statistics) => set({ positionStatistics: statistics }, false, 'setPositionStatistics'),

      // 获取账户数据
      fetchAccountData: async (timeRange: ProfitTimeRange = 'all') => {
        const { setPositionStatistics, isFetchingAccountData } = get();

        // 防止重复调用
        if (isFetchingAccountData) {
          console.log('⏳ 账户数据正在获取中，跳过重复调用');
          return;
        }

        try {
          set({ isFetchingAccountData: true });

          console.log(`🔍 开始获取账户统计数据 (${timeRange})...`);

          // 获取持仓统计数据
          const statisticsData = await accountService.getPositionStatistics(timeRange);

          // 处理统计数据
          if (statisticsData) {
            setPositionStatistics(statisticsData);
            console.log('✅ 持仓统计数据获取成功:', statisticsData);
          } else {
            console.error('❌ 获取账户统计数据为空');
            setPositionStatistics({
              todayPnL: 0,
              totalPnL: 0,
              totalTrades: 0,
              winRate: 0,
            });
          }
        } catch (error) {
          console.error('❌ 获取账户统计数据失败:', error);
          // 设置默认值
          setPositionStatistics({
            todayPnL: 0,
            totalPnL: 0,
            totalTrades: 0,
            winRate: 0,
          });
        } finally {
          set({ isFetchingAccountData: false });
        }
      },

      // 每日收益数据 actions
      setDailyProfits: (dailyProfits) => set({ dailyProfits }, false, 'setDailyProfits'),

      fetchDailyProfits: async (startDate, endDate) => {
        const { setLoading, setError, setDailyProfits, isFetchingDailyProfits } = get();

        // 防止重复调用
        if (isFetchingDailyProfits) {
          console.log('⏳ 每日收益数据正在获取中，跳过重复调用');
          return;
        }

        try {
          set({ isFetchingDailyProfits: true });
          setLoading(true);
          setError(null);

          console.log('🔍 开始获取每日收益数据...', {
            startDate,
            endDate,
            timestamp: new Date().toISOString(),
          });

          const response = await userProfitService.getDailyProfits(startDate, endDate);
          console.log('📊 每日收益数据响应:', response);

          if (response && response.dailyProfits) {
            setDailyProfits(response.dailyProfits || []);
            console.log('✅ 每日收益数据获取成功，数据条数:', (response.dailyProfits || []).length);
          } else {
            console.error('❌ v2 API返回错误或无数据:', response);
            setDailyProfits([]);
          }
        } catch (error) {
          console.error('❌ 获取每日收益数据失败:', error);
          let errorMessage = '获取每日收益数据失败';

          if (error instanceof Error) {
            if (error.message.includes('Network Error')) {
              errorMessage = '网络连接失败，请检查网络连接';
            } else if (error.message.includes('timeout')) {
              errorMessage = '请求超时，请稍后重试';
            } else {
              errorMessage = `获取每日收益数据失败: ${error.message}`;
            }
          }

          setError(errorMessage);
          setDailyProfits([]);
        } finally {
          setLoading(false);
          set({ isFetchingDailyProfits: false });
        }
      },

      // 用户收益数据 actions
      setUserProfitStats: (stats) => set({ userProfitStats: stats }, false, 'setUserProfitStats'),
      setUserProfitHistory: (history) => set({ userProfitHistory: history }, false, 'setUserProfitHistory'),
      setFundPoolStats: (stats) => set({ fundPoolStats: stats }, false, 'setFundPoolStats'),

      fetchUserProfitStats: async () => {
        const { setLoading, setError, setUserProfitStats, isFetchingUserProfit } = get();

        // 防止重复调用
        if (isFetchingUserProfit) {
          console.log('⏳ 用户收益统计正在获取中，跳过重复调用');
          return;
        }

        try {
          set({ isFetchingUserProfit: true });
          setLoading(true);
          setError(null);

          console.log('🔍 开始获取用户收益统计...');
          const data = await userProfitService.getUserProfitStats();
          console.log('📊 用户收益统计响应:', data);

          if (data) {
            console.log('📊 用户收益数据:', data);
            setUserProfitStats({
              totalAssets: parseFloat(data.totalAssets),
              totalProfit: parseFloat(data.totalProfit),
              todayProfit: parseFloat(data.todayProfit),
              profitRate: parseFloat(data.profitRate),
            });
            console.log('✅ 用户收益统计获取成功');
          } else {
            console.log('⚠️ 用户收益统计获取为空');
            // 新用户可能还没有收益数据，这是正常情况，默认显示0
            setUserProfitStats({
              totalAssets: 0,
              totalProfit: 0,
              todayProfit: 0,
              profitRate: 0,
            });
          }
        } catch (error: any) {
          console.error('❌ 获取用户收益统计失败:', error);
          // 新用户可能还没有收益数据，这是正常情况，不显示错误，默认显示0
          setUserProfitStats({
            totalAssets: 0,
            totalProfit: 0,
            todayProfit: 0,
            profitRate: 0,
          });
        } finally {
          setLoading(false);
          set({ isFetchingUserProfit: false });
        }
      },

      fetchUserProfitHistory: async (days = 30) => {
        const { setLoading, setError, setUserProfitHistory, isFetchingUserProfitHistory } = get();

        // 防止重复调用
        if (isFetchingUserProfitHistory) {
          console.log('⏳ 用户收益历史正在获取中，跳过重复调用');
          return;
        }

        try {
          set({ isFetchingUserProfitHistory: true });
          setLoading(true);
          setError(null);

          console.log(`🔍 开始获取用户收益历史（${days}天）...`);
          const response = await userProfitService.getUserProfitHistory(days);
          console.log('📊 用户收益历史响应:', response);

          if (response) {
            setUserProfitHistory(response || []);
            console.log('✅ 用户收益历史获取成功');
          } else {
            console.log('⚠️ 用户收益历史获取失败或为空');
            setUserProfitHistory([]);
          }
        } catch (error: any) {
          console.error('❌ 获取用户收益历史失败:', error);
          // 新用户可能还没有收益历史数据，这是正常情况，不显示错误，默认显示空数组
          setUserProfitHistory([]);
        } finally {
          setLoading(false);
          set({ isFetchingUserProfitHistory: false });
        }
      },

      fetchFundPoolStats: async () => {
        const { setLoading, setError, setFundPoolStats, isFetchingFundPool } = get();

        // 防止重复调用
        if (isFetchingFundPool) {
          console.log('⏳ 资金池统计正在获取中，跳过重复调用');
          return;
        }

        try {
          set({ isFetchingFundPool: true });
          setLoading(true);
          setError(null);

          console.log('🔍 开始获取资金池统计...');
          const response = await userProfitService.getFundPoolStats();
          console.log('📊 资金池统计响应:', response);

          if (response) {
            setFundPoolStats(response);
            console.log('✅ 资金池统计获取成功');
          } else {
            console.log('⚠️ 资金池统计获取失败或为空');
            setFundPoolStats(null);
          }
        } catch (error: any) {
          console.error('❌ 获取资金池统计失败:', error);
          setError(`获取资金池统计失败: ${error.message}`);
          setFundPoolStats(null);
        } finally {
          setLoading(false);
          set({ isFetchingFundPool: false });
        }
      },

      // 钱包相关 actions
      fetchWallets: async () => {
        const { setWallets, setLoading, setError, isFetchingWallet } = get();

        if (isFetchingWallet) {
          return;
        }

        try {
          set({ isFetchingWallet: true });
          setLoading(true);
          setError(null);

          console.log('🔍 开始获取所有钱包信息...');
          const data = await walletService.getWallets();
          console.log('📊 钱包列表数据:', data);

          setWallets(data);
          console.log('✅ 钱包列表获取成功');
        } catch (error: any) {
          console.error('❌ 获取钱包列表失败:', error);
          setError(error.message);
        } finally {
          setLoading(false);
          set({ isFetchingWallet: false });
        }
      },

      fetchWallet: async (currency: string = 'USDT', chain: string = 'POLYGON', sync: boolean = false) => {
        const { setWallet, setLoading, setError, isFetchingWallet } = get();

        if (isFetchingWallet) {
          console.log('⏳ 钱包数据正在获取中，跳过重复调用');
          return;
        }

        try {
          set({ isFetchingWallet: true });
          setLoading(true);
          setError(null);

          console.log(`🔍 开始获取钱包信息 (${currency} on ${chain}, sync=${sync})...`);
          const data = await walletService.getPrimaryWallet(currency, chain, sync);
          console.log('📊 钱包数据:', data);

          if (data) {
            setWallet(data);
            console.log('✅ 钱包信息获取成功');
          } else {
            console.warn('⚠️ 未找到钱包，可能需要创建');
            setWallet(null);
          }
        } catch (error: any) {
          console.error('❌ 获取钱包信息失败:', error);
          setWallet(null);
        } finally {
          setLoading(false);
          set({ isFetchingWallet: false });
        }
      },

      createWallet: async (currency: string = 'USDT', chain: string = 'POLYGON') => {
        const { setWallet, setLoading, setError, fetchWallets } = get();

        try {
          setLoading(true);
          setError(null);

          console.log(`🆕 正在创建钱包 (${currency} on ${chain})...`);
          const data = await walletService.createWallet(currency, chain);
          console.log('📊 创建钱包数据:', data);

          setWallet(data);
          await fetchWallets(); // 创建成功后重新加载所有钱包
          console.log('✅ 钱包创建成功');
          return data;
        } catch (error: any) {
          console.error('❌ 创建钱包失败:', error);
          setError(error.message);
          throw error;
        } finally {
          setLoading(false);
        }
      },

      deleteWallet: async (id: number) => {
        const { setLoading, setError, fetchWallets, wallet, setWallet } = get();
        try {
          setLoading(true);
          setError(null);
          console.log(`🗑️ 正在删除钱包 (ID: ${id})...`);
          await walletService.deleteWallet(id);

          // 如果删除的是当前选中的钱包，清空当前钱包
          if (wallet?.id === id) {
            setWallet(null);
          }

          await fetchWallets(); // 重新加载钱包列表
          console.log('✅ 钱包删除成功');
        } catch (error: any) {
          console.error('❌ 删除钱包失败:', error);
          setError(error.message);
          throw error;
        } finally {
          setLoading(false);
        }
      },

      fetchTransactions: async (params = {}) => {
        const { setTransactions, setLoading, setError, isFetchingTransactions } = get();

        if (isFetchingTransactions) {
          console.log('⏳ 交易记录正在获取中，跳过重复调用');
          return;
        }

        try {
          set({ isFetchingTransactions: true });
          setLoading(true);
          setError(null);

          console.log('🔍 开始获取交易记录...', params);
          const data = await walletService.getTransactions(params);
          console.log('📊 交易记录数据:', data);

          setTransactions(data || []);
          console.log('✅ 交易记录获取成功');
        } catch (error: any) {
          console.error('❌ 获取交易记录失败:', error);
          setTransactions([]);
        } finally {
          setLoading(false);
          set({ isFetchingTransactions: false });
        }
      },

      // 认证相关 actions
      setAuthToken: (token) => {
        set({ authToken: token }, false, 'setAuthToken');
        if (token) {
          localStorage.setItem('authToken', token);
        } else {
          localStorage.removeItem('authToken');
        }
      },
      setAuthUser: (user) => set({ authUser: user }, false, 'setAuthUser'),
      setAuthenticated: (isAuthenticated) => set({ isAuthenticated }, false, 'setAuthenticated'),

      telegramLogin: async (initData) => {
        const { setLoading, setError, setAuthToken, setAuthUser, setAuthenticated, isAuthenticating } = get();

        console.log('🔍 认证状态检查:', {
          isAuthenticating,
          isAuthenticated: get().isAuthenticated,
          authToken: get().authToken,
        });

        try {
          console.log('🚀 设置认证状态为进行中...');
          set({ isAuthenticating: true });
          setLoading(true);
          setError(null);

          console.log('🔐 开始 Telegram 用户认证...');
          console.log('📡 调用 telegramRegister 函数...');
          const response = await authService.telegramRegister(initData);
          console.log('✅ telegramRegister 调用完成');

          // 调试响应结构
          console.log('📊 Telegram 注册响应结构:', {
            response: response,
          });

          if (response && response.token && response.user) {
            console.log('✅ 条件判断通过，开始处理认证成功逻辑');
            const { token, user } = response;

            console.log('💾 保存 token 到 localStorage');
            localStorage.setItem('authToken', token);

            console.log('🔄 更新应用状态');
            setAuthToken(token);
            setAuthUser(user);
            setAuthenticated(true);

            console.log('✅ Telegram 用户认证成功:', user);
            console.log('🎯 telegramLogin 函数返回 true');
            return true;
          } else {
            console.log('❌ 条件判断失败，抛出错误');
            throw new Error('认证失败: 无效的响应数据');
          }
        } catch (error) {
          console.error('❌ Telegram 用户认证失败:', error);

          let errorMessage = '用户认证失败';

          if (error instanceof Error) {
            if (error.message.includes('Network Error')) {
              errorMessage = '网络连接失败，请检查网络连接';
            } else if (error.message.includes('timeout')) {
              errorMessage = '请求超时，请稍后重试';
            } else {
              errorMessage = `认证失败: ${error.message}`;
            }
          }

          setError(errorMessage);
          setAuthenticated(false);
          return false;
        } finally {
          console.log('🧹 清理认证状态');
          setLoading(false);
          set({ isAuthenticating: false });
          console.log('✅ telegramLogin 函数执行完成');
        }
      },

      logout: () => {
        const { setAuthToken, setAuthUser, setAuthenticated } = get();

        // 清除 localStorage 中的 token
        localStorage.removeItem('authToken');

        setAuthToken(null);
        setAuthUser(null);
        setAuthenticated(false);

        console.log('👋 用户已登出');
      },

      checkAuthStatus: async () => {
        const { authToken, setAuthenticated, setAuthUser, setError } = get();

        if (!authToken) {
          setAuthenticated(false);
          return false;
        }

        try {
          console.log('🔍 检查认证状态...');
          const response = await authService.verifyToken();

          if (response) {
            const userPayload = response.user ?? null;

            setAuthenticated(true);
            setAuthUser(userPayload);
            console.log('✅ 认证状态有效');
            return true;
          } else {
            setAuthenticated(false);
            setAuthUser(null);
            console.log('❌ 认证状态无效');
            return false;
          }
        } catch (error) {
          console.error('❌ 检查认证状态失败:', error);
          setAuthenticated(false);
          setAuthUser(null);
          setError('认证状态检查失败');
          return false;
        }
      },

      // UI 状态 actions
      setLoading: (loading) => set({ loading }, false, 'setLoading'),
      setError: (error) => set({ error }, false, 'setError'),
      setCurrentPage: (currentPage) => set({ currentPage }, false, 'setCurrentPage'),
      setPlaybackSymbol: (symbol: string) => set({ playbackSymbol: symbol }, false, 'setPlaybackSymbol'),
      setPlaybackRange: (range) => set({ playbackRange: range }, false, 'setPlaybackRange'),

      // 初始化应用
      initializeApp: async () => {
        const { setUser, setLoading, setError, isAppInitialized } = get();

        // 防止重复初始化
        if (isAppInitialized) {
          console.log('⏳ 应用已经初始化，跳过重复初始化');
          return;
        }

        try {
          set({ isAppInitialized: true });
          setLoading(true);
          setError(null);

          // 获取 Telegram 用户信息
          const user = getTelegramUser();
          if (user) {
            setUser(user);
            console.log('✅ 用户信息加载成功:', user);
          } else {
            setError('无法获取用户信息');
            console.error('❌ 无法获取用户信息');
          }
        } catch (error) {
          console.error('❌ 应用初始化失败:', error);
          setError('应用初始化失败');
        } finally {
          setLoading(false);
        }
      },

      // 清空所有状态
      clearAllState: () => {
        console.log('🧹 清空所有应用状态');
        set(
          {
            user: null,
            wallet: null,
            isAuthenticated: false,
            authToken: null,
            authUser: null,
            transactions: [],
            profitRecords: [],
            marketData: [],
            recentPriceAlerts: [],
            loading: false,
            error: null,
            currentPage: 'dashboard',
            isFetchingMarketData: false,
            isAppInitialized: false,
            isMarketDataInitialized: false,
            accountPositionsHistory: null,
            positionStatistics: null,
            isFetchingAccountData: false,
            dailyProfits: null,
            isFetchingDailyProfits: false,
            userProfitStats: null,
            userProfitHistory: null,
            fundPoolStats: null,
            isFetchingUserProfit: false,
            isFetchingUserProfitHistory: false,
            isFetchingFundPool: false,
            isFetchingAvailableCoins: false,
            isAuthenticating: false,
          },
          false,
          'clearAllState',
        );
      },
    }),
    {
      name: 'crypto-quant-app-store', // 用于 Redux DevTools
    },
  ),
);

// 选择器 hooks - 用于优化性能
export const useUser = () => useAppStore((state: AppState) => state.user);
export const useWallet = () => useAppStore((state: AppState) => state.wallet);
export const useMarketData = () => useAppStore((state: AppState) => state.marketData);
export const useRecentPriceAlerts = () => useAppStore((state: AppState) => state.recentPriceAlerts);
export const useLoading = () => useAppStore((state: AppState) => state.loading);
export const useError = () => useAppStore((state: AppState) => state.error);
export const useCurrentPage = () => useAppStore((state: AppState) => state.currentPage);
export const useTransactions = () => useAppStore((state: AppState) => state.transactions);
export const useProfitRecords = () => useAppStore((state: AppState) => state.profitRecords);

// 账户数据选择器 hooks
export const useAccountPositionsHistory = () => useAppStore((state: AppState) => state.accountPositionsHistory);
export const usePositionStatistics = () => useAppStore((state: AppState) => state.positionStatistics);
export const useIsFetchingAccountData = () => useAppStore((state: AppState) => state.isFetchingAccountData);

// 每日收益数据选择器 hooks
export const useDailyProfits = () => useAppStore((state: AppState) => state.dailyProfits);
export const useIsFetchingDailyProfits = () => useAppStore((state: AppState) => state.isFetchingDailyProfits);

// Actions hooks - 分别导出以避免对象重新创建
export const useSetUser = () => useAppStore((state: AppState) => state.setUser);
export const useSetWallet = () => useAppStore((state: AppState) => state.setWallet);
export const useSetTransactions = () => useAppStore((state: AppState) => state.setTransactions);
export const useAddTransaction = () => useAppStore((state: AppState) => state.addTransaction);
export const useSetProfitRecords = () => useAppStore((state: AppState) => state.setProfitRecords);
export const useAddProfitRecord = () => useAppStore((state: AppState) => state.addProfitRecord);
export const useSetMarketData = () => useAppStore((state: AppState) => state.setMarketData);
export const useFetchMarketData = () => useAppStore((state: AppState) => state.fetchMarketData);
export const useFetchAccountData = () => useAppStore((state: AppState) => state.fetchAccountData);
export const useFetchDailyProfits = () => useAppStore((state: AppState) => state.fetchDailyProfits);
export const useSetLoading = () => useAppStore((state: AppState) => state.setLoading);
export const useSetError = () => useAppStore((state: AppState) => state.setError);
export const useSetCurrentPage = () => useAppStore((state: AppState) => state.setCurrentPage);
export const useInitializeApp = () => useAppStore((state: AppState) => state.initializeApp);

// 认证相关选择器 hooks
export const useIsAuthenticated = () => useAppStore((state: AppState) => state.isAuthenticated);
export const useAuthToken = () => useAppStore((state: AppState) => state.authToken);
export const useAuthUser = () => useAppStore((state: AppState) => state.authUser);
export const useIsAuthenticating = () => useAppStore((state: AppState) => state.isAuthenticating);

// 认证相关 Actions hooks
export const useSetAuthToken = () => useAppStore((state: AppState) => state.setAuthToken);
export const useSetAuthUser = () => useAppStore((state: AppState) => state.setAuthUser);
export const useSetAuthenticated = () => useAppStore((state: AppState) => state.setAuthenticated);
export const useTelegramLogin = () => useAppStore((state: AppState) => state.telegramLogin);
export const useLogout = () => useAppStore((state: AppState) => state.logout);
export const useCheckAuthStatus = () => useAppStore((state: AppState) => state.checkAuthStatus);

// 用户收益数据选择器 hooks
export const useUserProfitStats = () => useAppStore((state: AppState) => state.userProfitStats);
export const useUserProfitHistory = () => useAppStore((state: AppState) => state.userProfitHistory);
export const useFundPoolStats = () => useAppStore((state: AppState) => state.fundPoolStats);
export const useIsFetchingUserProfit = () => useAppStore((state: AppState) => state.isFetchingUserProfit);
export const useIsFetchingUserProfitHistory = () => useAppStore((state: AppState) => state.isFetchingUserProfitHistory);
export const useIsFetchingFundPool = () => useAppStore((state: AppState) => state.isFetchingFundPool);

// 用户收益数据 Actions hooks
export const useSetUserProfitStats = () => useAppStore((state: AppState) => state.setUserProfitStats);
export const useSetUserProfitHistory = () => useAppStore((state: AppState) => state.setUserProfitHistory);
export const useSetFundPoolStats = () => useAppStore((state: AppState) => state.setFundPoolStats);
export const useFetchUserProfitStats = () => useAppStore((state: AppState) => state.fetchUserProfitStats);
export const useFetchUserProfitHistory = () => useAppStore((state: AppState) => state.fetchUserProfitHistory);
export const useFetchFundPoolStats = () => useAppStore((state: AppState) => state.fetchFundPoolStats);
